/**
 * @fileoverview Auto-save hook for text editor
 * 
 * Manages auto-save functionality with configurable intervals
 */

import { useEffect, useRef, useCallback } from 'react';

/**
 * Auto-save hook
 * 
 * @param {Object} config - Editor configuration
 * @param {Object} editorState - Current editor state
 * @param {boolean} isSaving - Whether manual save is in progress
 * @returns {Object} Auto-save utilities
 */
export function useAutoSave(config, editorState, isSaving) {
  const autoSaveTimeoutRef = useRef(null);
  const lastAutoSaveRef = useRef(null);

  /**
   * Clear auto-save timeout
   */
  const clearAutoSaveTimeout = useCallback(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
  }, []);

  /**
   * Schedule auto-save
   */
  const scheduleAutoSave = useCallback(() => {
    if (!config.autoSave || isSaving) return;

    clearAutoSaveTimeout();

    autoSaveTimeoutRef.current = setTimeout(() => {
      if (editorState.hasUnsavedChanges) {
        lastAutoSaveRef.current = Date.now();
        // Auto-save is handled by the parent component through localStorage
        // This hook just manages the timing
      }
    }, config.autoSaveInterval);
  }, [config.autoSave, config.autoSaveInterval, editorState.hasUnsavedChanges, isSaving, clearAutoSaveTimeout]);

  /**
   * Get time since last auto-save
   */
  const getTimeSinceLastAutoSave = useCallback(() => {
    if (!lastAutoSaveRef.current) return null;
    return Date.now() - lastAutoSaveRef.current;
  }, []);

  // Schedule auto-save when content changes
  useEffect(() => {
    if (editorState.hasUnsavedChanges) {
      scheduleAutoSave();
    }

    return clearAutoSaveTimeout;
  }, [editorState.hasUnsavedChanges, scheduleAutoSave, clearAutoSaveTimeout]);

  // Cleanup on unmount
  useEffect(() => {
    return clearAutoSaveTimeout;
  }, [clearAutoSaveTimeout]);

  return {
    scheduleAutoSave,
    clearAutoSaveTimeout,
    getTimeSinceLastAutoSave,
    isAutoSaveEnabled: config.autoSave
  };
}